import React, { useState } from 'react';
import { Crown, Users, Shield } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || '/';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      // Successful login
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-purple/5 to-royal-purple/10 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        {/* Logo and welcome text */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-royal-purple/20 rounded-full blur-lg" />
              <Crown className="w-12 sm:w-16 h-12 sm:h-16 text-royal-purple relative" />
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold text-royal-purple">
              RoyaltySuite
            </h1>
          </div>

          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto mb-8 px-4">
            Welcome to your exclusive social circle. Connect, share, and engage with your royal community in a space designed for distinction.
          </p>

          <div className="hidden sm:flex justify-center gap-12 mb-12">
            <div className="flex items-center gap-4">
              <div className="bg-royal-purple/10 p-3 rounded-lg">
                <Crown className="w-6 h-6 text-royal-purple" />
              </div>
              <span className="text-lg text-gray-600">Exclusive member-only content</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-royal-purple/10 p-3 rounded-lg">
                <Users className="w-6 h-6 text-royal-purple" />
              </div>
              <span className="text-lg text-gray-600">Private networking opportunities</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-royal-purple/10 p-3 rounded-lg">
                <Shield className="w-6 h-6 text-royal-purple" />
              </div>
              <span className="text-lg text-gray-600">Curated experiences</span>
            </div>
          </div>

          {/* Mobile features */}
          <div className="grid grid-cols-1 gap-4 mb-8 sm:hidden px-4">
            <div className="flex items-center gap-3 bg-white/50 p-3 rounded-lg">
              <div className="bg-royal-purple/10 p-2 rounded-lg">
                <Crown className="w-5 h-5 text-royal-purple" />
              </div>
              <span className="text-sm text-gray-600">Exclusive member-only content</span>
            </div>
            <div className="flex items-center gap-3 bg-white/50 p-3 rounded-lg">
              <div className="bg-royal-purple/10 p-2 rounded-lg">
                <Users className="w-5 h-5 text-royal-purple" />
              </div>
              <span className="text-sm text-gray-600">Private networking opportunities</span>
            </div>
            <div className="flex items-center gap-3 bg-white/50 p-3 rounded-lg">
              <div className="bg-royal-purple/10 p-2 rounded-lg">
                <Shield className="w-5 h-5 text-royal-purple" />
              </div>
              <span className="text-sm text-gray-600">Curated experiences</span>
            </div>
          </div>
        </div>

        {/* Login form */}
        <div className="w-full max-w-sm mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
            <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
              {error && (
                <div className="p-3 sm:p-4 rounded-lg bg-red-50 text-red-600 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="w-full px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-royal-purple focus:border-transparent text-sm sm:text-base"
                  />
                </div>

                <div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="w-full px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-royal-purple focus:border-transparent text-sm sm:text-base"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 sm:py-3 bg-royal-purple text-white rounded-lg font-medium hover:bg-royal-purple/90 transition-colors text-sm sm:text-base"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs sm:text-sm text-gray-500 hover:text-royal-purple"
                >
                  Forgot password?
                </Link>
              </div>

              <div className="text-center text-xs sm:text-sm text-gray-500">
                or
              </div>

              <Link
                to="/signup"
                className="block w-full py-2.5 sm:py-3 text-center border-2 border-royal-purple text-royal-purple rounded-lg font-medium hover:bg-royal-purple/5 transition-colors text-sm sm:text-base"
              >
                Create new account
              </Link>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}